import React, {useEffect, useRef, useState} from "react";

interface WidgetDataProps {
  datas: any[];
  openPopup: (row?: any) => void;
  handleDelete: (row?: any) => void;
}

const WidgetData: React.FC<WidgetDataProps> = ({
  datas,
  openPopup,
  handleDelete
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openIndex !== null &&
        buttonRefs.current[openIndex] &&
        !buttonRefs.current[openIndex]?.contains(event.target as Node)
      ) {
        setOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openIndex]);

  const alldatas = datas.filter((data: any) =>
    data.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPagination = ({
    totalItems,
    currentPage,
    pageSize,
    siblingCount = 1
  }: {
    totalItems: number;
    currentPage: number;
    pageSize: number;
    siblingCount?: number;
  }): (number | string)[] => {
    const totalPages = Math.ceil(totalItems / pageSize);

    if (totalPages <= 1) return [];

    const totalPageNumbers = siblingCount * 2 + 5;

    if (totalPageNumbers >= totalPages) {
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPages - 1
    );

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const pagination: (number | string)[] = [];

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;

      for (let i = 1; i <= leftItemCount; i++) {
        pagination.push(i);
      }

      pagination.push("...", totalPages);
    } else if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;

      pagination.push(1, "...");

      for (let i = totalPages - rightItemCount + 1; i <= totalPages; i++) {
        pagination.push(i);
      }
    } else {
      pagination.push(1, "...");

      for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
        pagination.push(i);
      }

      pagination.push("...", totalPages);
    }

    return pagination;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const totalItems = alldatas.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const overalldatas = alldatas.slice(start, end);
  const pagination = getPagination({totalItems, currentPage, pageSize});

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === "number" && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <div className="mb-2">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearch}
          className="w-full rounded border px-2 py-1"
        />
      </div>
      <div className="space-y-2">
        {overalldatas.map((row, index) => (
          <div
            key={index}
            className="flex items-center justify-between rounded-lg border bg-gray-50 p-3"
          >
            <div>
              <p className="text-sm font-medium">{row.name}</p>
              <p className="text-xs text-gray-500">{row.address}</p>
            </div>
            <div
              ref={el => {
                buttonRefs.current[index] = el;
              }}
              className="relative"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
              >
                <span className="text-xl leading-none text-gray-400">⋮</span>
              </button>
              {openIndex === index && (
                <div className="absolute left-5 z-50 min-w-32 -translate-x-full rounded-xl border bg-white p-1 shadow-lg">
                  <button
                    onClick={() => {
                      openPopup(row);
                      setOpenIndex(null);
                    }}
                    className="w-full whitespace-nowrap rounded-md p-2 text-left text-sm text-gray-700 hover:bg-[#F5A524]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      handleDelete(row);
                      setOpenIndex(null);
                    }}
                    className="w-full whitespace-nowrap rounded-md p-2 text-left text-sm text-gray-700 hover:bg-[#F5A524]"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <p>
          Showing {start + 1}–{Math.min(end, totalItems)} of {totalItems}{" "}
          Records
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-30"
          >
            &lt;
          </button>
          {pagination.map((page, idx) =>
            page === "..." ? (
              <span key={`dots-${idx}`} className="px-2">
                ...
              </span>
            ) : (
              <button
                key={`page-${page}`}
                onClick={() => handlePageClick(Number(page))}
                className={`rounded px-2 py-1 ${
                  page === currentPage
                    ? "bg-blue-100 text-blue-600"
                    : "hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-30"
          >
            &gt;
          </button>
        </div>
      </div>
    </>
  );
};

export default WidgetData;
